import { validateEntity as validateCombatEntity } from './CanonicalValidation';
import IntelligentContentParser from './IntelligentContentParser';
import { isAmmunitionEntry, isExplicitlyEquippedWeapon } from './EquipmentState';
import {
  buildShortAbilityBlock,
  collectCombatantSources,
  resolveCombatantArmorClass,
  resolveCombatantSpeedFeet,
  resolvePreferredAbilityScores
} from './combat/CombatantNormalizer';

const toNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const match = value.match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeHitPoints = (character) => {
  const currentCandidates = [
    character?.hp,
    character?.hpObj?.current,
    character?.hitPoints?.current,
    character?.hp?.current,
    character?.hp?.value,
    character?.hp?.average
  ];
  const maxCandidates = [
    character?.maxHp,
    character?.hpObj?.max,
    character?.hitPoints?.maximum,
    character?.hitPoints?.max,
    character?.hp?.maximum,
    character?.hp?.max,
    character?.hp?.average
  ];

  let current = null;
  for (const candidate of currentCandidates) {
    const numeric = toNumber(candidate);
    if (numeric !== null) {
      current = numeric;
      break;
    }
  }

  let max = null;
  for (const candidate of maxCandidates) {
    const numeric = toNumber(candidate);
    if (numeric !== null) {
      max = numeric;
      break;
    }
  }

  if (current === null && max !== null) current = max;
  if (max === null && current !== null) max = current;

  return { current, max };
};

const normalizeHitPointsFromSources = (character) => {
  const sources = collectCombatantSources(character);

  for (const source of sources) {
    const normalized = normalizeHitPoints(source);
    if (normalized.current !== null || normalized.max !== null) {
      return normalized;
    }
  }

  return { current: null, max: null };
};

const normalizeArmorClass = (character) => {
  const acCandidates = [
    character?.ac,
    character?.ac?.value,
    character?.ac?.base,
    character?.armorClass,
    character?.combatStats?.armorClass
  ];

  for (const candidate of acCandidates) {
    const numeric = toNumber(candidate);
    if (numeric !== null) {
      return numeric;
    }
  }

  return null;
};

const selectPreferredObject = (character, resolver) => {
  const sources = collectCombatantSources(character);

  for (const source of sources) {
    const candidate = resolver(source);
    if (Array.isArray(candidate)) {
      if (candidate.length > 0) return candidate;
      continue;
    }
    if (candidate && typeof candidate === 'object') {
      if (Object.keys(candidate).length > 0) return candidate;
      continue;
    }
    if (candidate != null) {
      return candidate;
    }
  }

  return null;
};

const normalizeSpeed = (speed) => {
  const numericSpeed = toNumber(speed);
  if (numericSpeed !== null) return numericSpeed;

  if (speed && typeof speed === 'object') {
    const walkCandidates = [
      speed.walk,
      speed.ground,
      speed.land,
      speed.base,
      speed.value
    ];

    for (const candidate of walkCandidates) {
      const numeric = normalizeSpeed(candidate);
      if (numeric !== null) {
        return numeric;
      }
    }

    for (const candidate of Object.values(speed)) {
      const numeric = normalizeSpeed(candidate);
      if (numeric !== null) {
        return numeric;
      }
    }
  }

  return null;
};

const resolveCharacterLevel = (character) => {
  const directLevel = toNumber(character?.level);
  if (directLevel !== null) return directLevel;

  if (Array.isArray(character?.classes) && character.classes.length > 0) {
    const totalLevels = character.classes.reduce((sum, cls) => {
      const level = toNumber(cls?.level);
      return level !== null ? sum + level : sum;
    }, 0);
    if (totalLevels > 0) return totalLevels;
  }

  return null;
};

const deriveProficiencyBonus = (character, level) => {
  const explicit = toNumber(character?.proficiencyBonus ?? character?.combatStats?.proficiencyBonus);
  if (explicit !== null) {
    return { value: explicit, derived: false };
  }

  if (level !== null) {
    return {
      value: Math.ceil(level / 4) + 1,
      derived: true
    };
  }

  return { value: null, derived: false };
};

const resolveExplicitProficiencyBonus = (character) => {
  const sources = collectCombatantSources(character);

  for (const source of sources) {
    const numeric = toNumber(source?.proficiencyBonus ?? source?.combatStats?.proficiencyBonus);
    if (numeric !== null && numeric >= 0) {
      return numeric;
    }
  }

  return null;
};

const deriveInitiative = (character, abilities) => {
  const explicit = toNumber(character?.initiative ?? character?.combatStats?.initiative);
  if (explicit !== null) {
    return { value: explicit, derived: false };
  }

  if (typeof abilities?.dexterity === 'number') {
    return {
      value: Math.floor((abilities.dexterity - 10) / 2),
      derived: true
    };
  }

  return { value: null, derived: false };
};

const normalizeEquipment = (equipment) => {
  const getTypeLabel = (entry) => String(
    entry?.type
    || entry?.kind
    || entry?.category
    || entry?.itemType
    || entry?.entryType
    || ''
  ).trim().toLowerCase();
  const looksLikeWeaponEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false;

    const typeLabel = getTypeLabel(entry);
    const itemName = String(entry?.name || '').trim();
    if (isAmmunitionEntry(entry)) return false;
    if (/\bweapon\b/i.test(typeLabel)) return true;
    if (/(armor|shield|consumable|potion|tool|kit|focus|wondrous|gear|valuable|currency|coin|gem|jewel)/i.test(typeLabel)) {
      return false;
    }

    const combinedText = [
      entry?.name,
      entry?.description,
      entry?.notes,
      entry?.statLine
    ].filter((part) => typeof part === 'string' && part.trim()).join('\n');

    return Boolean(
      /\b(axe|bow|crossbow|dagger|dart|flail|glaive|halberd|hammer|handaxe|javelin|lance|mace|maul|morningstar|pike|quarterstaff|rapier|scimitar|shortbow|shortsword|sling|spear|staff|sword|trident|war ?pick|warhammer|whip)\b/i.test(itemName)
      || /\b[+-]?\d+\s+to\s+hit\b/i.test(combinedText)
      || /\b\d+d\d+(?:\s*[+-]\s*\d+)?\b/i.test(combinedText)
      || /\b(range|reach)\s+\d+/i.test(combinedText)
      || /\b(cleave|graze|nick|push|sap|slow|topple|vex)\b/i.test(combinedText)
      || typeof entry?.attackBonus === 'number'
      || typeof entry?.toHit === 'number'
      || typeof entry?.damageFormula === 'string'
      || typeof entry?.damage === 'string'
      || entry?.attack
    );
  };
  const createBuckets = () => ({
    weapons: [],
    armor: [],
    items: [],
    valuables: [],
    currency: {}
  });
  const bucketEntry = (buckets, entry, sourceCollection = 'equipment') => {
    if (!entry) return;
    if (typeof entry !== 'object') {
      buckets.items.push(entry);
      return;
    }

    const explicitWeaponCollection = /(?:^|\.)(?:weapons|equippedWeapons)$/.test(String(sourceCollection));
    if (
      !isAmmunitionEntry(entry)
      && (
        explicitWeaponCollection
        || (isExplicitlyEquippedWeapon(entry, sourceCollection) && looksLikeWeaponEntry(entry))
      )
    ) {
      buckets.weapons.push(IntelligentContentParser.normalizeEquipmentWeaponEntry({
        ...entry,
        type: 'weapon'
      }));
      return;
    }

    const typeLabel = getTypeLabel(entry);
    if (typeLabel === 'armor' || typeLabel === 'shield') {
      buckets.armor.push(entry);
      return;
    }
    if (typeLabel === 'valuable') {
      buckets.valuables.push(entry);
      return;
    }

    buckets.items.push(entry);
  };

  if (Array.isArray(equipment)) {
    const buckets = createBuckets();
    equipment.forEach((entry) => bucketEntry(buckets, entry, 'equipment'));
    return buckets;
  }

  if (!equipment || typeof equipment !== 'object') {
    return createBuckets();
  }

  const buckets = createBuckets();
  (Array.isArray(equipment.weapons) ? equipment.weapons : [])
    .forEach((entry) => bucketEntry(buckets, entry, 'equipment.weapons'));
  (Array.isArray(equipment.armor) ? equipment.armor : [])
    .forEach((entry) => bucketEntry(buckets, entry, 'equipment.armor'));
  (Array.isArray(equipment.items) ? equipment.items : [])
    .forEach((entry) => bucketEntry(buckets, entry, 'equipment.items'));
  (Array.isArray(equipment.valuables) ? equipment.valuables : [])
    .forEach((entry) => bucketEntry(buckets, entry, 'equipment.valuables'));
  (Array.isArray(equipment.other) ? equipment.other : [])
    .forEach((entry) => bucketEntry(buckets, entry, 'equipment.other'));
  buckets.currency = equipment.currency && typeof equipment.currency === 'object'
    ? { ...equipment.currency }
    : {};

  return buckets;
};

const extractActionArray = (character, field) => {
  if (!character || typeof character !== 'object') return [];

  const collected = [];
  const sources = collectCombatantSources(character);

  sources.forEach((source) => {
    if (field === 'actions') {
      if (Array.isArray(source.actions)) {
        collected.push(...source.actions);
      } else if (source.actions && Array.isArray(source.actions.actions)) {
        collected.push(...source.actions.actions);
      }
      return;
    }

    if (Array.isArray(source[field])) {
      collected.push(...source[field]);
    } else if (source.actions && Array.isArray(source.actions[field])) {
      collected.push(...source.actions[field]);
    }
  });

  return collected;
};

const normalizeActivationLabel = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

  if (!normalized) return null;
  if (normalized.includes('bonus')) return 'bonus action';
  if (normalized.includes('reaction')) return 'reaction';
  if (normalized.includes('free')) return 'free';
  if (normalized.includes('action')) return 'action';
  return null;
};

const getDefaultActivationForField = (field) => {
  if (field === 'bonusActions') return 'bonus action';
  if (field === 'reactions') return 'reaction';
  return 'action';
};

const getFieldForActivation = (value) => {
  const normalized = normalizeActivationLabel(value);
  if (normalized === 'bonus action') return 'bonusActions';
  if (normalized === 'reaction') return 'reactions';
  return 'actions';
};

const getActionEntryKey = (entry, field) => {
  if (!entry || typeof entry !== 'object') return '';
  const normalizedName = IntelligentContentParser.normalizeParsedEntryName(entry?.name || '');
  const activationType = String(
    entry?.activationType ||
    entry?.actionType ||
    entry?.sourceSection ||
    field
  ).toLowerCase();
  if (normalizedName) {
    return `${field}|${normalizedName}|${activationType}`;
  }
  if (entry?.importMeta?.smartPasteFingerprint) return entry.importMeta.smartPasteFingerprint;
  return IntelligentContentParser.buildParsedEntryFingerprint(entry, field);
};

const deriveStructuredActionEconomy = (character) => {
  const derived = {
    actions: [],
    bonusActions: [],
    reactions: []
  };
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  const pushDerivedEntry = (entry, sourceField) => {
    if (!entry || typeof entry !== 'object' || !entry.name) return;

    const normalizedEntry = IntelligentContentParser.buildParsedAbilityEntry(entry, {
      sourceSection: entry.sourceSection || sourceField,
      derivedFromFeature: true
    });

    if (!normalizedEntry) return;

    let targetField = null;
    if (normalizedEntry.activationType === 'action') targetField = 'actions';
    if (normalizedEntry.activationType === 'bonus action') targetField = 'bonusActions';
    if (normalizedEntry.activationType === 'reaction') targetField = 'reactions';
    if (!targetField) return;

    const entryKey = getActionEntryKey(normalizedEntry, sourceField);
    if (!entryKey || seenKeys[targetField].has(entryKey)) return;

    seenKeys[targetField].add(entryKey);
    derived[targetField].push(normalizedEntry);
  };

  // Canonical characters keep authored mechanics under entity.data. Inspect the
  // same source graph used by explicit actions so wrapped records do not lose
  // their feature action economy at the readiness boundary. date_of_change: 2026-08-11
  collectCombatantSources(character).forEach((source) => {
    ['classFeatures', 'features', 'feats', 'speciesTraits'].forEach((field) => {
      const entries = Array.isArray(source?.[field]) ? source[field] : [];
      entries.forEach((entry) => pushDerivedEntry(entry, field));
    });
  });

  return derived;
};

const normalizeExplicitActionEntry = (entry, field) => {
  if (!entry || typeof entry !== 'object') return null;

  const fallbackActivation = getDefaultActivationForField(field);
  const explicitActivation = normalizeActivationLabel(entry.activationType || entry.actionType);
  const inferredActivation = normalizeActivationLabel(
    IntelligentContentParser.inferActivationTypeFromEntry({
      ...entry,
      sourceSection: entry.sourceSection || ''
    })
  );
  const resolvedActivation = (
    explicitActivation && explicitActivation !== 'action'
      ? explicitActivation
      : (inferredActivation || explicitActivation || fallbackActivation)
  );
  const sourceSection = entry.sourceSection || field;

  return IntelligentContentParser.normalizeCombatActionEntry({
    ...entry,
    sourceSection,
    activationType: resolvedActivation,
    actionType: resolvedActivation,
    parserMeta: IntelligentContentParser.mergeParserMeta(entry.parserMeta, { sourceSection })
  }, { kind: field });
};

const collectStructuredExplicitActionEconomy = (character) => {
  const collected = {
    actions: [],
    bonusActions: [],
    reactions: []
  };
  const seenKeys = {
    actions: new Set(),
    bonusActions: new Set(),
    reactions: new Set()
  };

  ['actions', 'bonusActions', 'reactions'].forEach((field) => {
    extractActionArray(character, field).forEach((entry) => {
      const normalizedEntry = normalizeExplicitActionEntry(entry, field);
      if (!normalizedEntry) return;

      const targetField = getFieldForActivation(
        normalizedEntry.activationType
        || normalizedEntry.actionType
        || field
      );
      const entryKey = getActionEntryKey(normalizedEntry, targetField);
      if (entryKey && seenKeys[targetField].has(entryKey)) return;
      if (entryKey) {
        seenKeys[targetField].add(entryKey);
      }
      collected[targetField].push(normalizedEntry);
    });
  });

  return collected;
};

const combineActionEntries = (explicitEntries, derivedEntries, field) => {
  const combined = [];
  const seenKeys = new Set();
  const pushUnique = (entry) => {
    if (!entry || typeof entry !== 'object') return;
    const entryKey = getActionEntryKey(entry, field);
    if (entryKey && seenKeys.has(entryKey)) return;
    if (entryKey) seenKeys.add(entryKey);
    combined.push(entry);
  };

  (Array.isArray(derivedEntries) ? derivedEntries : []).forEach(pushUnique);
  (Array.isArray(explicitEntries) ? explicitEntries : []).forEach(pushUnique);

  return combined;
};

const dedupeIssues = (issues) => Array.from(new Set((issues || []).filter(Boolean)));

const INFORMATIONAL_WARNING_PATTERNS = [
  /^Initiative was inferred from Dexterity for combat use\.$/i,
  /^Proficiency bonus was inferred from level for combat use\.$/i
];

const normalizeActionArray = (entries, field) => (
  (Array.isArray(entries) ? entries : []).map((action) => {
    if (!action || typeof action !== 'object') return action;
    if (action.activationType || action.actionType) {
      return action;
    }

    const sourceSection = action.sourceSection || field;
    return IntelligentContentParser.normalizeCombatActionEntry({
      ...action,
      sourceSection,
      parserMeta: IntelligentContentParser.mergeParserMeta(action.parserMeta, { sourceSection })
    }, { kind: field });
  })
);

export const buildCombatReadyCharacterProjection = (character) => {
  const canonicalSources = collectCombatantSources(character).filter((source) => source !== character);
  const normalizedAbilities = resolvePreferredAbilityScores({
    runtimeSources: [
      character?.abilities,
      character?.stats,
      character
    ],
    canonicalSources,
    runtimeOverrideSource: character,
    fallbackValue: null
  });
  const hitPoints = normalizeHitPointsFromSources(character || {});
  const level = resolveCharacterLevel(character || {});
  const explicitProficiencyBonus = resolveExplicitProficiencyBonus(character || {});
  const proficiencyBonus = explicitProficiencyBonus != null
    ? { value: explicitProficiencyBonus, derived: false }
    : deriveProficiencyBonus(character || {}, level);
  const initiative = deriveInitiative(character || {}, normalizedAbilities);
  const resolvedSpeed = resolveCombatantSpeedFeet(character || {});
  const speed = resolvedSpeed != null
    ? resolvedSpeed
    : normalizeSpeed(character?.speed ?? character?.combatStats?.speed);
  const equipment = normalizeEquipment(
    selectPreferredObject(character, (source) => (
      source?.equipment
      ?? source?.inventory?.equipment
      ?? source?.inventory
      ?? source?.compat?.originalData?.equipment
      ?? null
    ))
  );
  const spells = selectPreferredObject(character, (source) => (
    source?.spells
    ?? source?.spellcasting
    ?? source?.compat?.originalData?.spells
    ?? source?.compat?.originalData?.spellcasting
    ?? null
  )) || {};
  const spellcasting = selectPreferredObject(character, (source) => (
    source?.spellcasting
    ?? source?.spells
    ?? source?.compat?.originalData?.spellcasting
    ?? source?.compat?.originalData?.spells
    ?? null
  )) || {};
  const explicitActionEconomy = collectStructuredExplicitActionEconomy(character);
  const derivedActionEconomy = deriveStructuredActionEconomy(character);
  const projectedActions = combineActionEntries(
    explicitActionEconomy.actions,
    derivedActionEconomy.actions,
    'actions'
  );
  const projectedBonusActions = combineActionEntries(
    explicitActionEconomy.bonusActions,
    derivedActionEconomy.bonusActions,
    'bonusActions'
  );
  const projectedReactions = combineActionEntries(
    explicitActionEconomy.reactions,
    derivedActionEconomy.reactions,
    'reactions'
  );

  return {
    name: character?.name || 'Unknown Character',
    hp: hitPoints.current,
    maxHp: hitPoints.max,
    hpObj: (hitPoints.current !== null || hitPoints.max !== null)
      ? {
          current: hitPoints.current ?? 0,
          max: hitPoints.max ?? hitPoints.current ?? 0
        }
      : undefined,
    ac: resolveCombatantArmorClass(character || {}) ?? normalizeArmorClass(character || {}),
    initiative: initiative.value,
    speed,
    proficiencyBonus: proficiencyBonus.value,
    abilities: normalizedAbilities,
    abilitiesShort: buildShortAbilityBlock(normalizedAbilities),
    equipment,
    actions: normalizeActionArray(projectedActions, 'actions'),
    bonusActions: normalizeActionArray(projectedBonusActions, 'bonusActions'),
    reactions: normalizeActionArray(projectedReactions, 'reactions'),
    spells,
    spellcasting,
    _combatReadiness: {
      derivedInitiative: initiative.derived,
      derivedProficiencyBonus: proficiencyBonus.derived,
      level
    }
  };
};

export const assessCharacterCombatReadiness = (character) => {
  const projectedCharacter = buildCombatReadyCharacterProjection(character);
  const validation = validateCombatEntity(projectedCharacter);
  const warnings = [...(validation.warnings || [])];
  const spellPool = projectedCharacter.spells || {};
  const hasSpells = Boolean(
    (Array.isArray(spellPool.spells) && spellPool.spells.length) ||
    (Array.isArray(spellPool.known) && spellPool.known.length) ||
    (Array.isArray(spellPool.prepared) && spellPool.prepared.length) ||
    (spellPool.byLevel && Object.values(spellPool.byLevel).some((list) => Array.isArray(list) && list.length))
  );
  const hasRecordedWeapon = Array.isArray(projectedCharacter.equipment?.weapons)
    && projectedCharacter.equipment.weapons.length > 0;

  if (projectedCharacter._combatReadiness?.derivedInitiative) {
    warnings.push('Initiative was inferred from Dexterity for combat use.');
  }

  if (projectedCharacter._combatReadiness?.derivedProficiencyBonus) {
    warnings.push('Proficiency bonus was inferred from level for combat use.');
  }

  if (
    projectedCharacter.actions.length === 0 &&
    projectedCharacter.bonusActions.length === 0 &&
    projectedCharacter.reactions.length === 0 &&
    !hasRecordedWeapon &&
    !hasSpells
  ) {
    warnings.push('No usable combat actions were found. Add an action, bonus action, reaction, weapon, or spell before starting combat.');
  }

  const spellcasting = projectedCharacter.spellcasting || {};
  const hasSpellcastingSupport = Boolean(
    spellcasting.ability ||
    toNumber(spellcasting.attackBonus) !== null ||
    toNumber(spellcasting.spellAttackBonus) !== null ||
    toNumber(spellcasting.saveDC) !== null ||
    toNumber(spellcasting.saveDc) !== null
  );

  if (hasSpells && !hasSpellcastingSupport) {
    warnings.push('Spells are present without spellcasting ability, attack bonus, or save DC; spell resolution may need manual review.');
  }

  const errors = dedupeIssues(validation.errors || []);
  const uniqueWarnings = dedupeIssues(warnings);

  return {
    valid: errors.length === 0,
    errors,
    warnings: uniqueWarnings,
    projectedCharacter: {
      ...projectedCharacter,
      _combatReadiness: undefined
    },
    dataQualityWarnings: [...errors, ...uniqueWarnings]
  };
};

export const classifyCombatReadiness = (assessment) => {
  const errors = Array.isArray(assessment?.errors) ? assessment.errors : [];
  const warnings = Array.isArray(assessment?.warnings) ? assessment.warnings : [];
  const informationalWarnings = warnings.filter((warning) => (
    INFORMATIONAL_WARNING_PATTERNS.some((pattern) => pattern.test(String(warning || '')))
  ));
  const blockingWarnings = warnings.filter((warning) => !informationalWarnings.includes(warning));

  return {
    errors,
    warnings,
    informationalWarnings,
    blockingWarnings,
    simulatorReady: errors.length === 0 && blockingWarnings.length === 0
  };
};

export const hydrateCharacterCombatState = (character) => {
  if (!character || typeof character !== 'object') return character;

  const assessment = assessCharacterCombatReadiness(character);
  const projected = assessment.projectedCharacter;
  const hydratedCharacter = {
    ...character,
    dataQualityWarnings: assessment.dataQualityWarnings
  };

  if ((hydratedCharacter.hp === null || hydratedCharacter.hp === undefined) && projected.hp !== null) {
    hydratedCharacter.hp = projected.hp;
  }

  if ((hydratedCharacter.maxHp === null || hydratedCharacter.maxHp === undefined) && projected.maxHp !== null) {
    hydratedCharacter.maxHp = projected.maxHp;
  }

  if (typeof hydratedCharacter.ac !== 'number' && projected.ac !== null) {
    hydratedCharacter.ac = projected.ac;
  }

  if ((hydratedCharacter.speed === null || hydratedCharacter.speed === undefined) && projected.speed !== null) {
    hydratedCharacter.speed = projected.speed;
  }

  if ((hydratedCharacter.initiative === null || hydratedCharacter.initiative === undefined) && projected.initiative !== null) {
    hydratedCharacter.initiative = projected.initiative;
  }

  if ((hydratedCharacter.proficiencyBonus === null || hydratedCharacter.proficiencyBonus === undefined) && projected.proficiencyBonus !== null) {
    hydratedCharacter.proficiencyBonus = projected.proficiencyBonus;
  }

  if (Object.keys(projected.abilities || {}).length > 0) {
    hydratedCharacter.abilities = {
      ...(character.abilities || {}),
      ...projected.abilities
    };
    hydratedCharacter.stats = {
      ...(character.stats || {}),
      ...buildShortAbilityBlock(projected.abilities)
    };
    hydratedCharacter.abilitiesShort = {
      ...(character.abilitiesShort || {}),
      ...buildShortAbilityBlock(projected.abilities)
    };
  }

  hydratedCharacter.actions = projected.actions;
  hydratedCharacter.bonusActions = projected.bonusActions;
  hydratedCharacter.reactions = projected.reactions;

  if (
    !hydratedCharacter.equipment
    || Array.isArray(hydratedCharacter.equipment)
    || (Array.isArray(hydratedCharacter.equipment?.weapons) && hydratedCharacter.equipment.weapons.length === 0)
  ) {
    hydratedCharacter.equipment = projected.equipment;
  }

  if ((!hydratedCharacter.spells || Object.keys(hydratedCharacter.spells).length === 0) && projected.spells) {
    hydratedCharacter.spells = projected.spells;
  }

  if ((!hydratedCharacter.spellcasting || Object.keys(hydratedCharacter.spellcasting).length === 0) && projected.spellcasting) {
    hydratedCharacter.spellcasting = projected.spellcasting;
  }

  return hydratedCharacter;
};

export const buildCombatReadinessMeta = (assessment, source, checkedAt = new Date().toISOString()) => ({
  valid: Boolean(assessment?.valid),
  errors: Array.isArray(assessment?.errors) ? [...assessment.errors] : [],
  warnings: Array.isArray(assessment?.warnings) ? [...assessment.warnings] : [],
  checkedAt,
  source
});

export const summarizeCombatReadinessIssues = (assessment, maxIssues = 2) => {
  const issues = Array.isArray(assessment?.dataQualityWarnings) ? assessment.dataQualityWarnings : [];
  if (!issues.length) return '';

  const visibleIssues = issues.slice(0, maxIssues);
  const remaining = issues.length - visibleIssues.length;
  return remaining > 0
    ? `${visibleIssues.join(' ')} (+${remaining} more)`
    : visibleIssues.join(' ');
};

export default assessCharacterCombatReadiness;
